import Backbone from '@polleverywhere/backbone'

/**
 * Model containing attributes for each player score object within the
 * leaderboard collection.
 */
export default class PlayerScore extends Backbone.Model {
  /**
   * Define default values for model attributes.
   * @return {Object}
   */
  get defaults () {
    return {
      abbreviation: '?',
      rank: 0,
      score: 0,
      discrete_scores: {},
      screen_name: 'Unknown'
    }
  }

  /**
   * Returns whether the player placed an answer for the specified question
   * regardless of correctness.
   * @param {string} participantKey Poll question's participant key
   * @return {boolean} True if an answer was placed for this question.
   */
  answered (participantKey) {
    if (!participantKey) { return false }
    return participantKey in this.get('discrete_scores')
  }

  /**
   * Returns whether the player answered the specified question correctly.
   * @param {string} participantKey Poll question's participant key
   * @return {boolean} True if answered correctly and earned points.
   */
  answeredCorrect (participantKey) {
    if (!participantKey) { return false }
    return this.scoreFor(participantKey) > 0
  }

  /**
   * Returns the player's score earned from the specified question.
   * @param {string} participantKey Poll question's participant key
   * @return {number}
   */
  scoreFor (participantKey) {
    if (!participantKey) { return 0 }
    return this.get('discrete_scores')[participantKey] || 0
  }

  /**
   * Returns the player's previous total score before the specified question.
   * @param {string} participantKey Poll question's participant key
   * @return {number}
   */
  scoreWithout (participantKey) {
    if (!participantKey) { return this.totalScore() }
    return this.totalScore() - this.scoreFor(participantKey)
  }

  /**
   * Returns the player's total score up to this point.
   * @return {number}
   */
  totalScore () {
    return this.get('score') || 0
  }

  /**
   * Returns the player's screen name. The value returned will either be what
   * was hydrated by the backend or the local hash-generated value when
   * screen names are disabled by the presenter.
   * @return {string}
   */
  screenName () {
    return !this.collection || this.collection.screenNamesEnabled
      ? this.get('screen_name')
      : this.collection.generateScreenName(this.id)
  }
}
